// Clients — 真實資料版（Route 8, PR #98）。

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, RefreshCw, ServerCrash, Building2, Inbox, Trash2, Users as UsersIcon, ArrowRight } from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import DataTable, { type Column } from "../components/DataTable.js";
import PaginationControl from "../components/PaginationControl.js";
import { usePagination } from "../lib/usePagination.js";
import EmptyState from "../components/EmptyState.js";
import TenantPickerEmptyState from "../components/TenantPickerEmptyState.js";
import Skeleton from "../components/Skeleton.js";
import Drawer from "../components/Drawer.js";
import { Field } from "../components/Field.js";
import {
  listClients, createClient, patchClient, archiveClient, friendlyClientsError,
  type ClientRow, type ClientStatus,
} from "../lib/clientsApi.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { useConfirmDialog } from "../components/ConfirmDialog.js";

const STATUS_BADGE: Record<ClientStatus, string> = { active: "badge-success", inactive: "badge-default" };
const STATUS_LABEL: Record<ClientStatus, string> = { active: "啟用", inactive: "停用" };

type ListState =
  | { status: "loading" }
  | { status: "ready"; rows: ClientRow[]; total: number }
  | { status: "error"; message: string };

type DrawerState = null | { mode: "new" } | { mode: "edit"; client: ClientRow };

export default function Clients(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const canWrite = session.scopes.includes("write:tenant") || session.scopes.includes("admin:tenant") || session.super_admin === true;
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [editing, setEditing] = useState<DrawerState>(null);
  const pag = usePagination({ urlPrefix: "clients_" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const r = await listClients({ tenantId: tenantParam, limit: pag.limit, offset: pag.offset });
      setState({ status: "ready", rows: r.items, total: r.total });
    } catch (err) {
      setState({ status: "error", message: friendlyClientsError(err) });
    }
  }, [tenantParam, pag.limit, pag.offset]);

  useEffect(() => { void load(); }, [load]);

  const columns: Column<ClientRow>[] = useMemo(() => [
    {
      key: "name", header: "客戶名稱", primary: true, width: "30%",
      render: (c) => (
        <div>
          <div className="font-medium text-text-primary">{c.name}</div>
          <div className="text-2xs text-text-muted font-mono">{c.id}</div>
        </div>
      ),
    },
    {
      key: "contact", header: "聯絡人",
      render: (c) => (
        <div>
          <div className="text-text-primary">{c.contact_name ?? "—"}</div>
          <div className="text-2xs text-text-muted font-mono">{c.contact_phone ?? c.contact_email ?? ""}</div>
        </div>
      ),
    },
    {
      // Clicking 案場數 deep-links into the sites list pre-filtered to
      // this client (the page shows a 「目前終端客戶」 chip on top so
      // the operator knows which client the list is scoped to). `0`
      // links too — it's a fast path to "show me this client's empty
      // site list so I can add the first one".
      key: "site_count", header: "案場數",
      render: (c) => (
        <Link
          to={`/dashboard/sites?client_id=${encodeURIComponent(c.id)}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 font-mono tabular-nums text-accent hover:underline"
          title={`查看 ${c.name} 的所有案場`}
        >
          {c.site_count}
          <ArrowRight className="h-3 w-3" />
        </Link>
      ),
    },
    {
      key: "status", header: "狀態",
      render: (c) => <span className={STATUS_BADGE[c.status]}>{STATUS_LABEL[c.status]}</span>,
    },
  ], []);

  if (session.super_admin && !tenantParam) {
    return (
      <>
        <PageHeader title="終端客戶" description="此單位下的客戶與聯絡資料" />
        <TenantPickerEmptyState />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="終端客戶"
        description="此單位下的客戶與聯絡資料"
        actions={
          <>
            <button className="btn-secondary text-sm" onClick={() => void load()} disabled={state.status === "loading"}>
              <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
            </button>
            {canWrite && (
              <button data-tour-id="clients.new" onClick={() => setEditing({ mode: "new" })} className="btn-primary text-sm">
                <Plus className="h-4 w-4" /> 新增客戶
              </button>
            )}
          </>
        }
      />
      {state.status === "loading" && <ListSkeleton />}
      {state.status === "error" && (
        <EmptyState icon={ServerCrash} title="客戶列表載入失敗" description={state.message}
          action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />
      )}
      {state.status === "ready" && state.rows.length === 0 && (
        <EmptyState icon={UsersIcon} title="這個單位還沒有任何客戶" description="建立第一個客戶之後，就可以為它配置案場與巡邏路線。"
          action={canWrite ? <button data-tour-id="clients.new" className="btn-primary text-sm" onClick={() => setEditing({ mode: "new" })}>新增客戶</button> : undefined} />
      )}
      {state.status === "ready" && state.rows.length > 0 && (
        <>
          <DataTable
            rows={state.rows}
            columns={columns}
            rowKey={(c) => c.id}
            searchableText={(c) => `${c.name} ${c.contact_name ?? ""} ${c.contact_phone ?? ""} ${c.contact_email ?? ""}`}
            onRowClick={(c) => setEditing({ mode: "edit", client: c })}
            serverPagination
          />
          <div className="card mt-3">
            <PaginationControl
              page={pag.page}
              pageSize={pag.limit}
              total={state.total}
              onPageChange={pag.setPage}
              onPageSizeChange={pag.setLimit}
            />
          </div>
        </>
      )}
      <Drawer
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.mode === "edit" ? `編輯 ${editing.client.name}` : "新增客戶"}
        description="客戶名稱、聯絡資料、狀態"
      >
        {editing && (
          <ClientForm
            mode={editing.mode}
            client={editing.mode === "edit" ? editing.client : undefined}
            tenantId={tenantParam}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); void load(); }}
            canArchive={canWrite}
          />
        )}
      </Drawer>
    </>
  );
}

function ClientForm({ mode, client, tenantId, onClose, onSaved, canArchive }: {
  mode: "new" | "edit"; client?: ClientRow; tenantId: string | null;
  onClose: () => void; onSaved: () => void; canArchive: boolean;
}): React.ReactElement {
  const [name, setName] = useState(client?.name ?? "");
  const [contactName, setContactName] = useState(client?.contact_name ?? "");
  const [contactPhone, setContactPhone] = useState(client?.contact_phone ?? "");
  const [contactEmail, setContactEmail] = useState(client?.contact_email ?? "");
  const [status, setStatus] = useState<ClientStatus>(client?.status ?? "active");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const body = {
        name: name.trim(),
        contact_name: contactName.trim() || null,
        contact_phone: contactPhone.trim() || null,
        contact_email: contactEmail.trim() || null,
        status,
      };
      if (mode === "new") await createClient(body, tenantId);
      else if (client) await patchClient(client.id, body, tenantId);
      onSaved();
    } catch (e) { setErr(friendlyClientsError(e)); } finally { setBusy(false); }
  };
  const dlg = useConfirmDialog();
  const archive = async () => {
    if (!client) return;
    if (!(await dlg.confirm(`確定要停用「${client.name}」？此客戶下的案場仍會保留歷史資料。`, { destructive: true, okLabel: "停用" }))) return;
    setBusy(true); setErr(null);
    try { await archiveClient(client.id, tenantId); onSaved(); }
    catch (e) { setErr(friendlyClientsError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Field label="客戶名稱" required><input data-tour-id="clients.field.name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="例如：新光物業" /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="聯絡人姓名"><input className="input" value={contactName} onChange={(e) => setContactName(e.target.value)} /></Field>
        <Field label="聯絡電話"><input className="input font-mono" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="例如：0912-345-678" /></Field>
      </div>
      <Field label="聯絡 Email"><input className="input font-mono" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} /></Field>
      <Field label="狀態">
        <select className="select" value={status} onChange={(e) => setStatus(e.target.value as ClientStatus)}>
          <option value="active">啟用</option><option value="inactive">停用</option>
        </select>
      </Field>
      {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}
      <div className="flex flex-row items-center justify-between gap-2">
        <div>
          {mode === "edit" && client && canArchive && (
            <button className="btn-danger text-sm" onClick={() => void archive()} disabled={busy}><Trash2 className="h-4 w-4" /> 停用</button>
          )}
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-sm" disabled={busy}>取消</button>
          <button data-tour-id="clients.save" onClick={() => void save()} className="btn-primary text-sm" disabled={busy || !name.trim()}>{busy ? "儲存中…" : "儲存"}</button>
        </div>
      </div>
    </div>
  );
}

function ListSkeleton(): React.ReactElement {
  return (
    <div className="card overflow-hidden">
      <div className="p-4 border-b border-border"><Skeleton className="h-9 w-72" /></div>
      <div className="divide-y divide-border">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-4 p-4">
            <div className="flex-1 space-y-1.5"><Skeleton className="h-4 w-1/3" /><Skeleton className="h-3 w-1/2" /></div>
            <Skeleton className="h-5 w-12" />
          </div>
        ))}
      </div>
    </div>
  );
}
